import React, { useState, useEffect } from 'react';
import {
  importStudents,
  downloadTemplate,
  getHostelAdmissionOptions,
  importHostelResidents,
  downloadHostelResidentsTemplate
} from '../api';
import { FiAlertTriangle } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';

function BulkImport() {
  const { can } = usePermissions();
  const canStudents = can('bulk_import_students', 'create');
  const canHostel = can('bulk_import_hostel', 'create');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [formData, setFormData] = useState({
    campus_id: '',
    course_id: '',
    course_level: '',
    year: new Date().getFullYear(),
    hasExistingCertificates: false
  });
  const [result, setResult] = useState(null);
  const [hostelFile, setHostelFile] = useState(null);
  const [hostelLoading, setHostelLoading] = useState(false);
  const [hostelResult, setHostelResult] = useState(null);
  const [hostelFormData, setHostelFormData] = useState({
    hostel_id: '',
    campus_id: '',
    course_id: '',
    course_level: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Pure lookup data (names for dropdowns) — fetched via the unguarded
      // meta/options endpoint so this works regardless of whether the
      // current role has the separate Campuses/Courses modules granted.
      const res = await getHostelAdmissionOptions();
      setCampuses(res.data.campuses || []);
      setCourses(res.data.courses || []);
      setLevels(res.data.levels || []);
      setHostels(res.data.hostels || []);
    } catch (error) {
      showMessage('error', 'Failed to load data');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'student_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      showMessage('success', 'Template downloaded successfully');
    } catch (error) {
      showMessage('error', 'Failed to download template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      showMessage('error', 'Please select a file');
      return;
    }

    // Only require dropdown values for non-existing data imports
    if (formData.hasExistingCertificates === false) {
      if (!formData.campus_id || !formData.course_id || !formData.course_level) {
        showMessage('error', 'Please select campus, course, and level for new data import');
        return;
      }
    }

    setLoading(true);
    setResult(null);

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('campus_id', formData.campus_id);
    uploadData.append('course_id', formData.course_id);
    uploadData.append('course_level', formData.course_level);
    uploadData.append('year', formData.year);
    uploadData.append('hasExistingCertificates', formData.hasExistingCertificates);

    try {
      const response = await importStudents(uploadData);
      setResult(response.data);
      showMessage('success', `Import completed: ${response.data.successCount} students imported`);
      setFile(null);
      document.getElementById('fileInput').value = '';
    } catch (error) {
      console.error('Import error:', error);
      let errorMsg = 'Import failed';
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Import timed out. The file may be too large or the server is not responding.';
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = `Import failed: ${error.message}`;
      }
      showMessage('error', errorMsg);
      setResult({ error: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleHostelFileChange = (e) => {
    setHostelFile(e.target.files[0]);
    setHostelResult(null);
  };

  const handleDownloadHostelTemplate = async () => {
    try {
      const response = await downloadHostelResidentsTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'hostel_residents_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      showMessage('success', 'Hostel resident template downloaded successfully');
    } catch (error) {
      showMessage('error', 'Failed to download hostel resident template');
    }
  };

  const handleHostelSubmit = async (e) => {
    e.preventDefault();

    if (!hostelFile) {
      showMessage('error', 'Please select a hostel resident CSV file');
      return;
    }

    if (!hostelFormData.hostel_id) {
      showMessage('error', 'Please select hostel for hostel resident import');
      return;
    }

    setHostelLoading(true);
    setHostelResult(null);

    const uploadData = new FormData();
    uploadData.append('file', hostelFile);
    uploadData.append('hostel_id', hostelFormData.hostel_id);
    uploadData.append('campus_id', hostelFormData.campus_id);
    uploadData.append('course_id', hostelFormData.course_id);
    uploadData.append('course_level', hostelFormData.course_level);

    try {
      const response = await importHostelResidents(uploadData);
      setHostelResult(response.data);
      showMessage('success', `Hostel resident import completed: ${response.data.successCount} records imported`);
      setHostelFile(null);
      const input = document.getElementById('hostelFileInput');
      if (input) input.value = '';
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Hostel resident import failed');
    } finally {
      setHostelLoading(false);
    }
  };

  return (
    <div>
      {!canStudents && !canHostel && (
        <div className="card"><div className="alert alert-warning" style={{ margin: 0 }}>You don't have permission to bulk-import students or hostel residents.</div></div>
      )}
      {canStudents && (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Bulk Import Students</h2>
          <button className="btn btn-success" onClick={handleDownloadTemplate}>
            Download Template
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <div className="alert alert-info">
            <strong>Instructions:</strong>
            <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
              <li>Download the CSV template using the button above</li>
              <li><strong style={{ color: '#d9534f' }}>IMPORTANT for Excel users:</strong> Format CNIC and Contact No columns as TEXT before entering data
                <ul style={{ marginTop: '5px', fontSize: '13px' }}>
                  <li>Select the CNIC/Contact columns → Right-click → Format Cells → Text</li>
                  <li>Or prefix numbers with single quote: '3740234567891</li>
                  <li>This prevents Excel from converting to scientific notation</li>
                </ul>
              </li>
              <li>Fill in the student data (Name is required, other fields are optional)</li>
              <li><strong>For NEW data:</strong> Leave Campus/Course/Level columns empty and select them below</li>
              <li><strong>For EXISTING data with certificates:</strong> 
                <ul style={{ marginTop: '5px' }}>
                  <li>Fill Campus, Course, Level, and Certificate No columns in CSV</li>
                  <li>Check "Has Existing Certificates" option below</li>
                  <li>System will match by name/code or use dropdown values as fallback</li>
                </ul>
              </li>
              <li>Upload the CSV file and click Import</li>
            </ul>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Campus *</label>
              <select
                className="form-select"
                value={formData.campus_id}
                onChange={(e) => setFormData({ ...formData, campus_id: e.target.value })}
                required
              >
                <option value="">Select Campus</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name} ({campus.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Course *</label>
              <select
                className="form-select"
                value={formData.course_id}
                onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                required
              >
                <option value="">Select Course</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name} ({course.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Course Level *</label>
              <select
                className="form-select"
                value={formData.course_level}
                onChange={(e) => setFormData({ ...formData, course_level: e.target.value })}
                required
              >
                <option value="">Select Level</option>
                {levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    Level {level.level} - {level.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Year *</label>
              <input
                type="number"
                className="form-input"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                required
                min="2000"
                max="2100"
              />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.hasExistingCertificates}
                onChange={(e) => setFormData({ ...formData, hasExistingCertificates: e.target.checked })}
              />
              <span className="form-label" style={{ marginBottom: 0 }}>
                Has Existing Certificate Numbers (check this if CSV contains Campus, Course, Level, and Certificate No)
              </span>
            </label>
            <p style={{ marginTop: '5px', color: '#666', fontSize: '14px', paddingLeft: '30px' }}>
              When checked, system will look up Campus/Course/Level from CSV columns. Dropdown values below are used as fallback.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Upload CSV File *</label>
            <div className="file-upload">
              <input
                id="fileInput"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
              />
              <label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
                {file ? (
                  <div>
                    <strong>{file.name}</strong>
                    <p style={{ marginTop: '5px', color: '#666', fontSize: '14px' }}>
                      Click to change file
                    </p>
                  </div>
                ) : (
                  <div>
                    <strong>Click to select CSV file</strong>
                    <p style={{ marginTop: '5px', color: '#666', fontSize: '14px' }}>
                      or drag and drop here
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Importing...' : 'Import Students'}
          </button>
        </form>

        {result && (
          <div style={{ marginTop: '20px' }}>
            <div className="card" style={{ background: '#f8f9fa' }}>
              <h3 className="card-title">Import Results</h3>
              <div style={{ marginTop: '15px' }}>
                <p><strong>Total Rows Parsed:</strong> {result.parsedRows || result.successCount + result.errorCount}</p>
                <p style={{ color: 'green' }}><strong>Successfully Imported:</strong> {result.successCount} students</p>
                <p style={{ color: 'red' }}><strong>Failed:</strong> {result.errorCount} students</p>
                
                {result.duplicates && result.duplicates.length > 0 && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#fff3cd', borderRadius: '5px', border: '1px solid #ffc107' }}>
                    <strong style={{ color: '#856404', display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FiAlertTriangle size={14} /> Duplicate Certificate Numbers ({result.duplicateCount}):</strong>
                    <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '10px' }}>
                      {result.duplicates.map((error, index) => (
                        <div key={index} style={{ padding: '5px', background: 'white', marginBottom: '5px', borderRadius: '3px', fontSize: '14px' }}>
                          <strong>Row {error.row}:</strong> {error.error}
                        </div>
                      ))}
                    </div>
                    <p style={{ marginTop: '10px', fontSize: '14px', color: '#856404' }}>
                      These rows were skipped. Other rows were imported successfully.
                    </p>
                  </div>
                )}
                
                {result.errors && result.errors.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong style={{ color: '#dc3545' }}>Other Errors:</strong>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                      {result.errors.map((error, index) => (
                        <div key={index} style={{ padding: '5px', background: 'white', marginBottom: '5px', borderRadius: '3px', border: '1px solid #dc3545' }}>
                          Row {error.row}: {error.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {canHostel && (
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <h2 className="card-title">Bulk Import Hostel Residents</h2>
          <button className="btn btn-success" onClick={handleDownloadHostelTemplate}>
            Download Resident Template
          </button>
        </div>

        <div className="alert alert-info" style={{ marginBottom: '20px' }}>
          <strong>Hostel Resident Import Instructions:</strong>
          <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
            <li>Use this section only for hostel resident records.</li>
            <li>CSV must include campus/course/level columns (recommended: campus_code, course_code, course_level).</li>
            <li>Dropdown campus/course/level values are optional and used as fallback if CSV values are missing.</li>
            <li>Admission date is required in CSV and is used for certificate year mapping later.</li>
            <li>After import, use the Residents page bulk selection to generate certificate numbers.</li>
          </ul>
        </div>

        <form onSubmit={handleHostelSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Hostel *</label>
              <select className="form-select" value={hostelFormData.hostel_id} onChange={(e) => setHostelFormData({ ...hostelFormData, hostel_id: e.target.value })} required>
                <option value="">Select Hostel</option>
                {hostels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Campus (Fallback)</label>
              <select className="form-select" value={hostelFormData.campus_id} onChange={(e) => setHostelFormData({ ...hostelFormData, campus_id: e.target.value })}>
                <option value="">Select Campus</option>
                {campuses.map((campus) => <option key={campus.id} value={campus.id}>{campus.name} ({campus.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Course (Fallback)</label>
              <select className="form-select" value={hostelFormData.course_id} onChange={(e) => setHostelFormData({ ...hostelFormData, course_id: e.target.value })}>
                <option value="">Select Course</option>
                {courses.map((course) => <option key={course.id} value={course.id}>{course.name} ({course.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Course Level (Fallback)</label>
              <select className="form-select" value={hostelFormData.course_level} onChange={(e) => setHostelFormData({ ...hostelFormData, course_level: e.target.value })}>
                <option value="">Select Level</option>
                {levels.map((level) => <option key={level.id} value={level.id}>Level {level.level} - {level.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Upload Hostel Resident CSV *</label>
            <div className="file-upload">
              <input id="hostelFileInput" type="file" accept=".csv" onChange={handleHostelFileChange} />
              <label htmlFor="hostelFileInput" style={{ cursor: 'pointer' }}>
                {hostelFile ? (
                  <div>
                    <strong>{hostelFile.name}</strong>
                    <p style={{ marginTop: '5px', color: '#666', fontSize: '14px' }}>Click to change file</p>
                  </div>
                ) : (
                  <div>
                    <strong>Click to select resident CSV file</strong>
                    <p style={{ marginTop: '5px', color: '#666', fontSize: '14px' }}>or drag and drop here</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={hostelLoading}>
            {hostelLoading ? 'Importing...' : 'Import Hostel Residents'}
          </button>
        </form>

        {hostelResult && (
          <div style={{ marginTop: '20px' }}>
            <div className="card" style={{ background: '#f8f9fa' }}>
              <h3 className="card-title">Hostel Import Results</h3>
              <div style={{ marginTop: '15px' }}>
                <p><strong>Total Rows Parsed:</strong> {hostelResult.parsedRows || hostelResult.successCount + hostelResult.errorCount}</p>
                <p style={{ color: 'green' }}><strong>Successfully Imported:</strong> {hostelResult.successCount} residents</p>
                <p style={{ color: 'red' }}><strong>Failed:</strong> {hostelResult.errorCount} residents</p>
                {hostelResult.errors && hostelResult.errors.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong style={{ color: '#dc3545' }}>Errors:</strong>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                      {hostelResult.errors.map((error, index) => (
                        <div key={index} style={{ padding: '5px', background: 'white', marginBottom: '5px', borderRadius: '3px', border: '1px solid #dc3545' }}>
                          Row {error.row}: {error.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export default BulkImport;
